import React, { useState, useEffect, useRef } from 'react';
import { useMount } from 'react-use';
import { useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment-timezone';
import _ from 'lodash';
import { useJsonToCsv } from 'react-json-csv';
import {
  Button,
  Table,
  Card,
  notification,
  Avatar,
  Tooltip,
  DatePicker,
} from 'antd';
import { Comment } from '@ant-design/compatible';
import dayjs from 'dayjs';
import { PageHeader } from '@ant-design/pro-layout';
import {
  CloudDownloadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { timezoneAtom, profileAtom, selectedOrderAtom, viewDetailsAtom } from '../../../../atoms/Atoms';
import { thousandv2, toCurrency, renderShortDateTime, mapOrderType, mapOrderStatus, mapPaymentType, renderShortDateHour, renderDate, formatPhoneNumber, mapOrderForDetailsModal } from '../../../utils/functions';
import api from '../../../../api/api';
import envConfig from '../../../../envConfig';
import { dateRangePresets } from '../../../utils/utils';
import Grocefy from '../../../../assets/images/grocefyLogoAlone.png';

function GiftCardsPurchaseHistory() {
  const { RangePicker } = DatePicker;
  const { t } = useTranslation();
  const { saveAsCsv } = useJsonToCsv();
  const timezone = useRecoilValue(timezoneAtom);
  const profile = useRecoilValueLoadable(profileAtom);
  const setSelectedOrder = useSetRecoilState(selectedOrderAtom);
  const setViewDetails = useSetRecoilState(viewDetailsAtom);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSize, setCurrentSize] = useState(20);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);

  const isSuperAdmin =
    profile.contents && profile?.contents?.roles?.includes('SuperAdmin');
  const isAdmin = isSuperAdmin ||
    (profile.contents && profile?.contents?.roles?.includes('CustomerSupportAdmin'));
  const isStoreManager =
    isAdmin ||
    (profile?.contents &&
      (profile?.contents?.roles?.includes('StoreAdmin') ||
        profile?.contents?.roles?.includes('Owner')));
  const canViewFinance =
    isAdmin ||
    isStoreManager ||
    (profile.contents &&
      (profile.contents.roles.includes('Finance') ||
        profile.contents.roles.includes('TopAnalytics')));
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: moment().startOf('month').format('YYYY-MM-DD'),
    end: moment().endOf('month').format('YYYY-MM-DD')
  });

  const [width, setWidth] = useState(window.innerWidth);

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function getData(page) {
    setLoading(true);
    const data = {
      StartDate: dayjs(dateRange.start).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hour').format('YYYY-MM-DDTHH:mm:ss.000'),
      EndDate: dayjs(dateRange.end).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hour').format('YYYY-MM-DDTHH:mm:ss.000'),
      Page: page,
      Size: currentSize,
      ExportAll: false
    };
    api
      .post(
        'giftcards/purchase/report',
        data
      )
      .then((response) => {
        setLoading(false);
        setReportData(response.data.data);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }

  function revert(id) {
    setLoadingOrder(true);
    let tempReportData = JSON.parse(JSON.stringify(reportData));
    for (let i = 0; i < tempReportData.transactions.length; i++) {
      const transaction = tempReportData.transactions[i];
      if (transaction.id === id) {
        transaction.loading = true;
      }
    }
    setReportData(tempReportData);
    api.post(`giftcards/revert/${id}`)
      .then((response) => {
        if (response.data.success) {
          setLoadingOrder(false);
          tempReportData = JSON.parse(JSON.stringify(reportData));
          for (let i = 0; i < tempReportData.transactions.length; i++) {
            const transaction = tempReportData.transactions[i];
            transaction.loading = false;
          }
          getData(currentPage);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        console.error(error);
        showMessage(error.message);
        setLoadingOrder(false);
        for (let i = 0; i < tempReportData.transactions.length; i++) {
          const transaction = tempReportData.transactions[i];
          transaction.loading = false;
        }
      });
  }

  function exportData() {
    setLoadingExport(true);
    const data = {
      StartDate: dayjs(dateRange.start).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hour').format('YYYY-MM-DDTHH:mm:ss.000'),
      EndDate: dayjs(dateRange.end).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hour').format('YYYY-MM-DDTHH:mm:ss.000'),
      Page: 0,
      Size: currentSize,
      ExportAll: true
    };
    api
      .post(
        'giftcards/purchase/report',
        data
      )
      .then((response) => {
        setLoadingExport(false);
        saveAsCsv({
          data: response.data.data.transactions,
          fields: {
            date: t('date'),
            code: t('code'),
            amount: t('amount'),
            giftCardCurrentBalance: t('current_balance'),
            confirmationCode: t('confirmation_code'),
            storeName: t('store_name'),
          },
          filename: `Gift Card Purchases ${moment(dateRange.start).format('MM/DD/YYYY')} - ${moment(dateRange.end).format('MM/DD/YYYY')}`,
        });
      })
      .catch((error) => {
        console.error(error);
        setLoadingExport(false);
      });
  }

  function loadOrder(orderId) {
    setLoadingOrder(true);
    let tempReportData = JSON.parse(JSON.stringify(reportData));
    for (let i = 0; i < tempReportData.transactions.length; i++) {
      const transaction = tempReportData.transactions[i];
      if (transaction.orderId === orderId) {
        transaction.loading = true;
      }
    }
    setReportData(tempReportData);
    api
      .get(`orders/order/${orderId}`)
      .then((response) => {
        setLoadingOrder(false);
        const order = response.data.data;
        tempReportData = JSON.parse(JSON.stringify(reportData));
        for (let i = 0; i < tempReportData.transactions.length; i++) {
          const transaction = tempReportData.transactions[i];
          transaction.loading = false;
        }
        setReportData(tempReportData);
        setViewDetails(true);
        setSelectedOrder(mapOrderForDetailsModal(order, timezone, t));
      })
      .catch((error) => {
        console.error(error);
        setLoadingOrder(false);
      });
  }

  const allColumns = [
    {
      title: '',
      key: 'mobile',
      align: 'center',
      className: 'text-xs md:hidden',
      render: (row) => (
        <div className="border border-1 p-2 grid gap-2 grid-cols-2">
          <div className="text-left col-span-2">
            <p className="font-bold">{t('day')}</p>
            <p>{renderShortDateTime(row.date, timezone)}</p>
          </div>

          <div className="text-left col-span-2">
            <p className="font-bold">{t('code')}</p>
            <p>{renderShortDateTime(row.code, timezone)}</p>
          </div>

          <div className="text-left">
            <p className="font-bold">{t('amount')}</p>
            <p>{toCurrency(row.amount)}</p>
          </div>
          <div className="text-right">
            <p className="font-bold">{t('final_balance')}</p>
            <p>{toCurrency(row.giftCardCurrentBalance)}</p>
          </div>

          <div className="text-left">
            <Button
              size="small"
              type="primary"
              loading={row.loading}
              disabled={loadingOrder}
              onClick={() => loadOrder(row?.confirmationCode)}
            >
              {row?.confirmationCode}
            </Button>
          </div>
          <div className="text-right">
            <p className="font-bold">{t('business')}</p>
            <p>{row.storeName}</p>
          </div>

          <div className="text-center col-span-2">
            {isAdmin && (
              <Button
                size="small"
                type="primary"
                loading={row.loading}
                disabled={loadingOrder}
                onClick={() => revert(row.id)}
              >
                {t('revert')}
              </Button>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('day'),
      dataIndex: 'date',
      key: 'createdAt',
      align: 'center',
      className: 'text-xs hidden md:table-cell',
      render: (text) => <span>{renderShortDateTime(text, timezone)}</span>,
    },
    {
      title: t('code'),
      dataIndex: 'code',
      key: 'code',
      align: 'center',
      className: 'text-xs',
      render: (text) => <span>{text}</span>,
    },
    {
      title: t('amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      className: 'text-xs hidden md:table-cell',
      render: (text) => <span>{toCurrency(text)}</span>,
    },
    {
      title: t('final_balance'),
      dataIndex: 'giftCardCurrentBalance',
      key: 'giftCardCurrentBalance',
      align: 'center',
      className: 'text-xs hidden md:table-cell',
      render: (text) => <span>{toCurrency(text)}</span>,
    },
    {
      title: t('order'),
      key: 'orderConfirmationCode',
      align: 'center',
      className: 'text-xs hidden md:table-cell',
      render: (row) => (
        <Button
          size="small"
          type="primary"
          loading={row.loading}
          disabled={loadingOrder}
          onClick={() => loadOrder(row?.confirmationCode)}
        >
          {row?.confirmationCode}
        </Button>
      ),
    },
    {
      title: t('business'),
      key: 'orderBusinessName',
      align: 'center',
      className: 'text-xs hidden md:table-cell',
      render: (row) => (
        <span>{row.storeName}</span>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      align: 'center',
      className: `${isAdmin ? '' : 'hidden'} text-xs hidden md:table-cell`,
      render: (row) => (
        <>
          <Button
            size="small"
            type="primary"
            loading={row.loading}
            disabled={loadingOrder}
            onClick={() => revert(row.id)}
          >
            {t('revert')}
          </Button>
        </>
      ),
    },
  ];

  useMount(() => {
    setCurrentPage(0);
    getData(0);
  });

  useEffect(() => {
    setWidth(window.innerWidth);
  }, [window.innerWidth]);

  const didLoadRef = useRef(false);
  useEffect(() => {
    if (!didLoadRef.current) {
      didLoadRef.current = true;
      return;
    }
    setCurrentPage(0);
    getData(0);
  }, [dateRange, currentSize]);

  return (
    <div>
      <PageHeader
        className="mb-4 px-0"
        title={t('purchase_history')}
        extra={
          [
            <RangePicker
              format="YYYY-MM-DD"
              value={[
                dayjs(dateRange?.start),
                dayjs(dateRange?.end)
              ]}
              disabled={loading || loadingExport}
              presets={dateRangePresets(t)}
              onChange={(date, dateString) => {
                setDateRange({
                  start: dateString[0],
                  end: dateString[1]
                });
              }}
            />,
            <div className="space-x-2">
              <br className={width < 1024 ? null : 'hidden'} />
              <Button
                type="primary"
                size="small"
                loading={loading}
                disabled={loading || loadingExport}
                icon={<ReloadOutlined />}
                onClick={() => {
                  setCurrentPage(0);
                  getData(0);
                }}
              >
                {t('refresh')}
              </Button>
            </div>,
            <Button
              type="primary"
              size="small"
              loading={loadingExport}
              disabled={loading || loadingExport}
              icon={<CloudDownloadOutlined />}
              onClick={() => {
                exportData();
              }}
            >
              {t('export')}
            </Button>
          ]
        }
      />
      <Table
        loading={loading}
        bordered
        pagination={{
          pageSize: currentSize,
          showSizeChanger: true,
          defaultCurrent: 0,
          current: currentPage + 1,
          total: reportData?.total,
          onChange: (page, pageSize) => {
            if (page - 1 !== currentPage) {
              setCurrentPage(page - 1);
              getData(page - 1);
            } else {
              setCurrentSize(pageSize);
            }
          }
        }}
        columns={allColumns}
        dataSource={reportData?.transactions}
      />
    </div>
  );
}

export default GiftCardsPurchaseHistory;
